// @ts-nocheck
import PropTypes from "prop-types";
import {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {toast} from "@/components";
import usePagoMovilRegistration from "../../../hooks/usePagoMovilRegistration";
import useResendEmailVerification from "../../../hooks/useResendEmailVerification";
import useWithdrawalPagoMovil from "../../../hooks/wallet/useWithdrawal.pagoMovil.hook";
import {useBalanceStore} from "../../../store/useBalance.store";
import CustomSteps from "../../CustomSteps";
import SelectAccount from "./components/SelectAccount";
import WithdrawalAmount from "./components/WithdrawalAmount";
import WithdrawalConfirm from "./components/WithdrawalConfirm";
import WithdrawalFailure from "./components/WithdrawalFailure";
import WithdrawalSuccess from "./components/WithdrawalSuccess";

const WithdrawalSteps = ({onBack, onCancel}) => {
  const [current, setCurrent] = useState(0);
  const [selectBankAccount, setSelectBankAccount] = useState({});
  const [withdrawalAmount, setWithdrawalAmount] = useState(0);
  const [isNextDisabled, setIsNextDisabled] = useState(true);

  // Result states for success/failure views
  const [withdrawalState, setWithdrawalState] = useState("form"); // "form" | "success" | "failure"
  const [failureErrorMessage, setFailureErrorMessage] = useState("");

  const isMobile = window.innerWidth <= 768;

  const {requestWithdrawal, isPending, isError, isSuccess, error} = useWithdrawalPagoMovil();
  const {triggerRegistrationModalIfNeeded} = usePagoMovilRegistration();
  const {resendVerification, isSuccess: isEmailSent, isError: isEmailError} = useResendEmailVerification();

  const handleWithdrawalModal = useBalanceStore(state => state.handleWithdrawalModal);

  const {t} = useTranslation();

  const steps = [
    {
      title: t("withdrawalStepsAccount"),
      content: (
        <SelectAccount
          setSelectBankAccount={setSelectBankAccount}
          setIsNextDisabled={setIsNextDisabled}
        />
      ),
    },
    {
      title: t("withdrawalStepsAmount"),
      content: (
        <WithdrawalAmount
          setWithdrawalAmount={setWithdrawalAmount}
          setIsNextDisabled={setIsNextDisabled}
        />
      ),
    },
    {
      title: t("withdrawalStepsWidthdraw"),
      content: (
        <WithdrawalConfirm
          selectBankAccount={selectBankAccount}
          withdrawalAmount={withdrawalAmount}
        />
      ),
    },
  ];

  const resetWithdrawalState = () => {
    setWithdrawalAmount(0);
    setSelectBankAccount({});
    setCurrent(0);
    setWithdrawalState("form");
    setFailureErrorMessage("");
  };

  // Helper to go back to form for retry (keeps the amount and account selected)
  const handleRetry = () => {
    setWithdrawalState("form");
    setFailureErrorMessage("");
  };

  const handleCloseModal = () => {
    resetWithdrawalState();
    handleWithdrawalModal();
  };

  const next = () => {
    setCurrent(current + 1);
  };

  const prev = () => {
    setCurrent(current - 1);
  };

  const handleConfirm = async () => {
    // Check if user needs pago mÃ³vil registration before proceeding
    const registrationResult = await triggerRegistrationModalIfNeeded("withdrawal");

    if (registrationResult) {
      toast.warning(`${t("withdrawalExplanationTitle")} — ${t("withdrawalExplanationMessage")}`);

      setTimeout(() => {
        resetWithdrawalState();
        handleWithdrawalModal();
      }, 1000);

      return;
    }

    requestWithdrawal({
      amount: Number(withdrawalAmount),
      pm_account_id: selectBankAccount?.id,
    });
  };

  // Helper function to get error message from axios error or regular error
  const getErrorMessage = err => {
    return err?.response?.data?.message || err?.message || "";
  };

  // Helper function to get user-friendly error description
  const getErrorDescription = errorMessage => {
    if (errorMessage.includes("minWageNotReached")) {
      return t("withdrawalStepsErrorDesc_1");
    }
    if (errorMessage.includes("emailNotVerified")) {
      return t("withdrawalStepsErrorDesc_3");
    }
    if (errorMessage.includes("minMaxNotReached")) {
      return t("withdrawalStepsErrorDesc_4");
    }
    return t("withdrawalStepsErrorDesc_2");
  };

  useEffect(() => {
    if (isError) {
      const errorMessage = getErrorMessage(error);
      const errorDescription = getErrorDescription(errorMessage);

      // If email not verified, automatically resend verification email
      if (errorMessage.includes("emailNotVerified")) {
        resendVerification();
      }

      // Set failure state with error message
      setFailureErrorMessage(errorDescription);
      setWithdrawalState("failure");
    } else if (isSuccess) {
      // Set success state
      setWithdrawalState("success");
    }
  }, [isError, isSuccess, error, t]);

  // Handle email verification success/error feedback
  useEffect(() => {
    if (isEmailSent) {
      toast.success(`${t("emailVerificationSentTitle")} — ${t("emailVerificationSentMessage")}`);
    }
    if (isEmailError) {
      toast.error(`${t("emailVerificationErrorTitle")} — ${t("emailVerificationErrorMessage")}`);
    }
  }, [isEmailSent, isEmailError, t]);

  const contentStyle = {
    textAlign: "center",
    color: "var(--color-text-secondary)",
    backgroundColor: "var(--color-surface)",
    borderRadius: "var(--radius-md)",
    marginTop: "var(--space-4-5)",
    padding: isMobile ? "12px 8px" : "16px 24px",
  };

  // Render success state
  if (withdrawalState === "success") {
    return (
      <div
        id="withdrawal-steps-container"
        style={{
          maxWidth: "100%",
          margin: "0 auto",
          padding: isMobile ? "0 0" : "10px 70px",
        }}
      >
        <WithdrawalSuccess
          amount={withdrawalAmount}
          onClose={handleCloseModal}
        />
      </div>
    );
  }

  // Render failure state
  if (withdrawalState === "failure") {
    return (
      <div
        id="withdrawal-steps-container"
        style={{
          maxWidth: "100%",
          margin: "0 auto",
          padding: isMobile ? "0 0" : "10px 70px",
        }}
      >
        <WithdrawalFailure
          errorMessage={failureErrorMessage}
          onRetry={handleRetry}
          onClose={handleCloseModal}
        />
      </div>
    );
  }

  // Render form state (default)
  // Accept onBack prop for returning to WithdrawalMethods
  // If current > 0, back goes to previous step; if current === 0, back returns to WithdrawalMethods
  return (
    <div
      id="withdrawal-steps-container"
      style={{
        maxWidth: "100%",
        margin: "0 auto",
        padding: isMobile ? "0 4px" : "0 24px",
      }}
    >
      <CustomSteps
        steps={steps}
        current={current}
      />
      <div style={contentStyle}>{steps[current].content}</div>
      <div className="flex items-center justify-between mt-5">
        <div className="flex items-center gap-2">
          {current < steps.length - 1 && (
            <button
              type="button"
              onClick={() => next()}
              disabled={isNextDisabled}
              className="px-6 py-2.5 rounded-full text-sm font-semibold text-white bg-[var(--color-lottery)] hover:opacity-90 transition-opacity disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {t("withdrawalStepsNext")}
            </button>
          )}
          {current === steps.length - 1 && (
            <button
              type="button"
              disabled={isPending}
              onClick={() => handleConfirm()}
              className="px-6 py-2.5 rounded-full text-sm font-semibold text-white bg-[var(--color-lottery)] hover:opacity-90 transition-opacity disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {t("withdrawalStepsConfirm")}
            </button>
          )}
          <button
            type="button"
            onClick={() => {
              if (current > 0) {
                prev();
              } else if (typeof onBack === "function") {
                resetWithdrawalState();
                onBack();
              }
            }}
            className="px-6 py-2.5 rounded-full text-sm font-semibold border border-[var(--color-lottery)] text-[var(--color-lottery)] bg-transparent hover:bg-[var(--color-lottery)]/10 transition-colors"
          >
            {t("withdrawalStepsBack")}
          </button>
        </div>
        <button
          type="button"
          onClick={() => {
            resetWithdrawalState();
            if (typeof onCancel === "function") onCancel();
          }}
          className="px-6 py-2.5 rounded-full text-sm font-semibold border border-[var(--color-lottery)] text-[var(--color-lottery)] bg-transparent hover:bg-[var(--color-lottery)]/10 transition-colors"
        >
          {t("withdrawalStepsCancel")}
        </button>
      </div>
    </div>
  );
};
export default WithdrawalSteps;

WithdrawalSteps.propTypes = {
  onBack: PropTypes.func,
  onCancel: PropTypes.func,
};
